import type * as models from './models'

/**
 * Presentation state isolated to one table instance. The config is absent
 * only until the required input's initial snapshot is synchronized; every
 * collection is replaced rather than mutated when local interaction changes.
 */
export interface State<TRow> {
  /** Widest rendered content measured for each current column. */
  readonly columnContentWidthsPx: ReadonlyMap<models.Column.Id, number>
  /**
   * User-defined column order, reconciled by stable column ID.
   *
   * `undefined` until the user reorders columns. Until then, the configured
   * column order is authoritative, so columns that arrive in a later config
   * snapshot take their configured position instead of being appended.
   */
  readonly columnOrder: readonly models.Column.Id[] | undefined
  /** Latest complete immutable snapshot supplied by the consumer. */
  readonly config?: models.Config<TRow>
  /** Hierarchy rows expanded in this table instance. */
  readonly expandedRowIds: ReadonlySet<models.Row.Id>
  /** Horizontal position of the data viewport. */
  readonly horizontalScrollLeftPx: number
  /** Width currently available inside the table viewport. */
  readonly hostWidthPx: number
  /** Fixed local widths retained for current column IDs after a manual resize. */
  readonly resizedColumnWidthsPx: ReadonlyMap<models.Column.Id, number>
  /** Whether the column settings overlay is currently open. */
  readonly settingsOpen: boolean
  /** Current local wrapping preference. */
  readonly wrap: boolean
}

export namespace State {
  export const INITIAL: Omit<State<never>, 'config'> = {
    columnContentWidthsPx: new Map(),
    columnOrder: undefined,
    expandedRowIds: new Set(),
    horizontalScrollLeftPx: 0,
    hostWidthPx: 0,
    resizedColumnWidthsPx: new Map(),
    settingsOpen: false,
    wrap: false,
  }
}
